import { formatDate, formatNumber } from "@wrstudios/utils";
import { getWeek } from "date-fns";
import React from "react";
import { Helmet } from "react-helmet";
import { useLive } from "../LiveProvider";
import LiveHeader from "../common/LiveHeader";
import LiveRdcBarChart from "./LiveRdcBarChart";
import LiveRdcPoweredBy from "./LiveRdcPoweredBy";
import LiveRdcValueGrid from "./LiveRdcValueGrid";
import {
  Container,
  PoweredByContainer,
  SubTitle,
  Title
} from "./styled/live-rdc";
import {
  ChartCard,
  Highlight,
  Stat,
  StatDescription,
  StatHeader,
  StatTitle,
  StatValue,
  Stats,
  StatsContainer
} from "./styled/live-rdc-matching-buyers";

function LiveRdcMatchingBuyers() {
  const { rdc } = useLive();

  const matchingBuyersInMarket = rdc.data?.buyers?.totalBuyers?.byBudget || 0;
  const totalBuyersInMarket = rdc.data?.buyers?.totalBuyers?.byMarket || 0;

  const matchingBuyersInMarketPercentage =
    rdc.data?.buyers?.totalBuyers?.byBudgetPercentage || 0;
  const totalBuyersInMarketPercentage =
    rdc.data?.buyers?.totalBuyers?.byMarketPercentage || 0;

  const chartValuesByWeek = (rdc.data?.buyers?.dailyBuyers || []).reduce(
    (weeks, buyer) => {
      const weekNumber = getWeek(new Date(buyer.eventDate));
      return {
        ...weeks,
        [weekNumber]: [
          ...(weeks[weekNumber] || []),
          {
            label: buyer.eventDate,
            value: buyer.byBudget
          }
        ]
      };
    },
    {}
  );
  const chartValues = Object.values(chartValuesByWeek).map((week) => {
    return {
      label: formatDate({ dateValue: week[0].label, formating: "M/d" }),
      value: week.reduce((total, day) => (total += day.value), 0)
    };
  });

  // How much space between the top of the chart and the max result?
  const maxChartValueBuffer = 5;

  // Limit the chart scale from 25 or grater
  const maxChartValue = Math.max(
    25,
    Math.max(...chartValues.map(({ value }) => value)) + maxChartValueBuffer
  );

  return (
    <>
      <Helmet>
        <title>Matching Buyers</title>
      </Helmet>
      <LiveHeader>Matching Buyers</LiveHeader>
      <Container large>
        <Title>
          Realtor.com® can match you with buyers searching for a property like
          yours in your area and have a similar price range.
        </Title>
        <StatsContainer>
          <Stats>
            <Stat>
              <StatHeader>Matching buyers in your market</StatHeader>
              <StatValue>{formatNumber(matchingBuyersInMarket)}</StatValue>
              {rdc.loading ? (
                <StatTitle>Loading...</StatTitle>
              ) : (
                <StatTitle>
                  {matchingBuyersInMarketPercentage > 1 ? (
                    <>
                      Up{" "}
                      <Highlight>
                        {formatNumber(
                          matchingBuyersInMarketPercentage * 100,
                          "0[.]0"
                        )}
                        %
                      </Highlight>
                    </>
                  ) : matchingBuyersInMarketPercentage < 1 ? (
                    <>
                      Down{" "}
                      <Highlight>
                        {formatNumber(
                          Math.abs(matchingBuyersInMarketPercentage * 100),
                          "0[.]0"
                        )}
                        %
                      </Highlight>
                    </>
                  ) : (
                    "No change"
                  )}{" "}
                  vs previous <Highlight>30</Highlight> days.
                </StatTitle>
              )}
              <StatDescription>
                A matching buyer is someone working with a Realtor.com® known
                agent to search for properties in your zip code, and whose
                budget matches your home's suggested price range.
              </StatDescription>
            </Stat>
            <Stat>
              <StatHeader>Total buyers in your market</StatHeader>
              <StatValue>{formatNumber(totalBuyersInMarket)}</StatValue>
              {rdc.loading ? (
                <StatTitle>Loading...</StatTitle>
              ) : (
                <StatTitle>
                  {totalBuyersInMarketPercentage > 1 ? (
                    <>
                      Up{" "}
                      <Highlight>
                        {formatNumber(
                          totalBuyersInMarketPercentage * 100,
                          "0[.]0"
                        )}
                        %
                      </Highlight>
                    </>
                  ) : totalBuyersInMarketPercentage < 1 ? (
                    <>
                      Down{" "}
                      <Highlight>
                        {formatNumber(
                          Math.abs(totalBuyersInMarketPercentage * 100),
                          "0[.]0"
                        )}
                        %
                      </Highlight>
                    </>
                  ) : (
                    "No change"
                  )}{" "}
                  vs previous <Highlight>30</Highlight> days.
                </StatTitle>
              )}
              <StatDescription>
                Total buyers are those working with a Realtor.com® known agent
                to search for properties of any price in your zip code.
              </StatDescription>
            </Stat>
          </Stats>
        </StatsContainer>
        <ChartCard>
          <SubTitle>Matching buyers by week</SubTitle>
          <LiveRdcBarChart maxValue={maxChartValue} values={chartValues} />
        </ChartCard>
        <SubTitle>Why is this valuable?</SubTitle>
        <LiveRdcValueGrid
          items={[
            "The buyer demand is a signal of how the market might look by the time you list your home, especially if there's strong (or not so strong) demand in your area.",
            "We can focus our time on the buyers who have a matching budget and are looking to buy in the local market."
          ]}
        />
        <PoweredByContainer>
          <LiveRdcPoweredBy />
        </PoweredByContainer>
      </Container>
    </>
  );
}

export default LiveRdcMatchingBuyers;
